import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, updateDoc, doc, serverTimestamp, increment } from 'firebase/firestore';

// Friendly labels + descriptions for the AI generation prompts. Docs are
// auto-seeded by the Cloud Functions the first time each generator runs.
const PROMPT_INFO = {
  'core6-system-prompt': {
    label: 'Brand Voice System Prompt',
    desc: 'Governs the writing voice for ALL blog articles and title generation — brand rules, banned words, banned sentence patterns.',
  },
  'core6-system-prompt-es': {
    label: 'Brand Voice System Prompt — Spanish',
    desc: 'Governs the writing voice for Spanish-language blog articles: Mexican Spanish rules, banned Spanish words, banned translated-English sentence patterns. Only used by queue items set to Spanish.',
  },
  'outrank-titles': {
    label: 'Outrank Title Generation',
    desc: 'Generates blog titles + talking points from client data on the Content Queue page.',
  },
  'report-summary': {
    label: 'Monthly Report Summary',
    desc: 'Writes the client-facing monthly performance email from an uploaded marketing report.',
  },
};

export default function SystemPrompts() {
  const [docs, setDocs] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [draft, setDraft] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'systemPrompts'), snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => a.id.localeCompare(b.id));
      setDocs(all);
    });
    return unsub;
  }, []);

  const openEditor = (p) => {
    if (expandedId === p.id) { setExpandedId(null); return; }
    setDraft(p.content || '');
    setExpandedId(p.id);
  };

  const savePrompt = async (p) => {
    if (!draft.trim()) { alert('Prompt cannot be empty.'); return; }
    // Guard: warn if any {{tokens}} present in the original were removed
    const oldTokens = [...(p.content || '').matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1]);
    const missing = oldTokens.filter(t => !draft.includes(`{{${t}}}`));
    if (missing.length > 0 && !window.confirm(
      `Warning: you removed the placeholder${missing.length > 1 ? 's' : ''} ${missing.map(t => `{{${t}}}`).join(', ')}. ` +
      'The system fills these with live client data — removing them means that data will be missing from generations. Save anyway?'
    )) return;

    setSavingId(p.id);
    try {
      await updateDoc(doc(db, 'systemPrompts', p.id), {
        content: draft,
        version: increment(1),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || 'unknown',
      });
      setSavedId(p.id);
      setTimeout(() => setSavedId(null), 2500);
      setExpandedId(null);
    } catch (e) {
      console.error('Failed to save prompt:', e);
      alert('Save failed. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const fmtDate = (ts) => {
    if (!ts) return '';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return ''; }
  };

  return (
    <div className="card" style={{ marginBottom: 20, border: '1px solid #4f46e544' }}>
      <div className="card-title" style={{ color: '#818cf8', marginBottom: 4 }}>✦ AI Generation Prompts</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        The prompts behind blog writing, title generation, and report summaries. Edits take effect on the next generation — no deploy needed. Keep the {'{{placeholders}}'} intact; they're filled with live client data.
      </div>

      {docs.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>
          No prompts seeded yet — they appear here automatically after each generator runs once.
        </div>
      )}

      {docs.map(p => {
        const info = PROMPT_INFO[p.id] || { label: p.name || p.id, desc: '' };
        const isOpen = expandedId === p.id;
        return (
          <div key={p.id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => openEditor(p)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {info.label}
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#818cf8', background: 'rgba(99,102,241,.1)', border: '1px solid #4f46e533', borderRadius: 10, padding: '1px 8px', marginLeft: 8 }}>
                    v{p.version || 1}
                  </span>
                  {savedId === p.id && <span style={{ fontSize: 11, color: '#4ade80', fontWeight: 600, marginLeft: 8 }}>✓ Saved</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {info.desc}
                  {p.updatedAt && <span> · Updated {fmtDate(p.updatedAt)}{p.updatedBy && p.updatedBy !== 'system-seed' ? ` by ${p.updatedBy}` : ''}</span>}
                </div>
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{isOpen ? '▾ Close' : '▸ Edit'}</span>
            </div>

            {isOpen && (
              <div style={{ marginTop: 10 }}>
                <textarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  spellCheck={false}
                  style={{ width: '100%', minHeight: 320, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.55, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="btn btn-primary btn-sm" disabled={savingId === p.id} onClick={() => savePrompt(p)}>
                    {savingId === p.id ? 'Saving…' : `Save as v${(p.version || 1) + 1}`}
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setExpandedId(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
